import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReCaptcha from "@tCooneyBrothers/Components/ReCaptcha";
import submitContactForm from "@tCooneyBrothers/Handlers/SubmitContactForm";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapDispatchToProps = {
    submitContactForm,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    contactUsBackground?: InjectableCss;
}

export const contactFormStyles: ContactFormStyles = {
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            left: calc(100% - 80px);
            position: relative;
            margin-top: 20px;
        `,
    },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    contactUsBackground: {
        css: css`
            background-color: #fafafa;
            padding: 20px;
            > div[class^="Wrapper"] {
                div[class^="FormFieldStyle"] {
                    margin-bottom: 15px;

                    &:last-of-type {
                        margin-bottom: 0;
                    }
                    > label {
                        position: absolute;
                        z-index: 1;
                        left: 20px;
                        background: #fafafa;
                        width: auto;
                        top: 0px;
                        min-height: auto;
                    }
                }
            }
        `,
    },
};

const styles = contactFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const ContactForm: React.FC<Props> = ({ id, fields, submitContactForm, clearForm, validateReCaptcha }: Props) => {
    const { emailRecipients, submitButtonText } = fields;
    const [validators] = React.useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }
        // Get the current URL
        const urlParams = new URLSearchParams(window.location.search);
        // Get the value of 'flag'
        const flag = urlParams.get("flag");
        const isFromContest = flag === "true";

        // Determine type
        const type = window.location.pathname.includes("/reorder")
            ? "Reorder"
            : isFromContest
              ? "ContestPage"
              : "ContactUs";

        if (results.every(o => o)) {
            setIsSubmitting(true);
            submitContactForm({
                emailRecipients,
                type,
                onSuccess: () => {
                    clearForm();
                    setIsSubmitting(false);
                    window.location.href = isFromContest ? "/promo-thank-you" : "/ThankYou";
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                },
            });
        }
    };

    useEffect(() => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "contact_us",
            });
        }
    }, []);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <ContactFormContext.Provider value={{ validators }}>
                    <form id="contactForm" onSubmit={handleFormSubmit} noValidate>
                        <StyledWrapper {...styles.contactUsBackground}>
                            <Zone contentId={id} zoneName="Content" />
                            <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                            <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                {submitButtonText}
                            </Button>
                        </StyledWrapper>
                    </form>
                </ContactFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ContactForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: emailsRegexp,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
